import PropTypes from 'prop-types';
import { AMFI_COLORS, amfiItemStyles } from './styles';

// Import type-specific components
import AttributeContent from './types/AttributeContent';
import MetricContent from './types/MetricContent';
import FactContent from './types/FactContent';
import InsightContent from './types/InsightContent';

// Import common components
import ActionMenu from './common/ActionMenu';
import Footer from './common/Footer';

/**
 * Component for displaying a single AMFI (Attribute, Metric, Fact, or Insight) item
 * with proper styling and action buttons.
 */
const AMFIItem = ({
	item,
	type, // 'attribute', 'metric', 'fact', or 'insight'
	onEdit,
	onDelete,
	onReclassify,
	onToggleKey,
}) => {
	// Handler to call the parent's onToggleKey function
	const handleToggleKey = (item) => {
		onToggleKey(item);
	};

	// Format attribute name from snake_case or camelCase to Title Case
	const formatName = (name) => {
		if (!name) return '';

		// Handle pre-formatted names (already containing spaces)
		if (name.includes(' ')) return name;

		// Format snake_case or camelCase to Title Case
		return name
			.replace(/([A-Z])/g, ' $1') // Insert space before capital in camelCase
			.replace(/_/g, ' ') // Replace underscores with spaces
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ')
			.trim();
	};
	// Format metric values based on unit type
	const formatMetricValue = (metric) => {
		if (!metric || (metric.value === undefined && metric.value !== 0)) {
			return '—';
		}

		const value = metric.value;
		const unit = metric.unit?.toLowerCase();

		// Handle currency
		if (unit === 'usd' || unit === 'currency' || unit === '$') {
			const numValue = parseFloat(value);
			if (isNaN(numValue)) return value;

			// Format large numbers
			if (numValue >= 1000000) {
				return `$${(numValue / 1000000).toFixed(1)}M`;
			} else if (numValue >= 1000) {
				return `$${(numValue / 1000).toFixed(0)}K`;
			}
			return `$${numValue.toLocaleString()}`;
		}

		// Handle percentages
		if (unit === '%' || unit === 'percentage' || unit === 'percent') {
			const numValue = parseFloat(value);
			return isNaN(numValue) ? value : `${numValue}%`;
		}

		// Handle count/number
		if (unit === 'count' || unit === 'number') {
			const numValue = parseFloat(value);
			return isNaN(numValue) ? value : numValue.toLocaleString();
		}

		// Default - return as is with unit if available
		return unit ? `${value} ${unit}` : value;
	};

	// Determine if we're using 'value' or 'content' field based on type
	// TODO: use it and remove leading underscore, or remove it
	const _displayValue = () => {
		if (type === 'attribute') {
			return item.value;
		} else if (type === 'metric') {
			return formatMetricValue(item);
		} else {
			return item.content;
		}
	};

	// Neutral colors for non-key items
	const neutralColors = {
		main: '#9e9e9e', // Neutral gray
		bg: 'rgba(158, 158, 158, 0.1)', // Very light gray background
		light: 'rgba(158, 158, 158, 0.1)',
	};

	// Get type-specific colors from imported AMFI_COLORS, but only use for key items
	const typeColors = AMFI_COLORS[type];

	// Choose colors based on whether the item is a key item
	const colors = item.is_key ? typeColors : neutralColors;

	// Capitalize type for display
	// TODO: use it and remove leading underscore, or remove it
	const _displayType = type.charAt(0).toUpperCase() + type.slice(1);

	// Get the appropriate display text for the header
	const getHeaderText = () => {
		// For metrics, show the formatted value and unit in the header
		if (type === 'metric') {
			return formatMetricValue(item);
		}

		// For facts, show a preview of the content instead of a name
		if (type === 'fact') {
			if (!item.content) return 'Unnamed Fact';

			// Truncate content for preview in header (first 30 chars)
			const contentPreview = item.content.substring(0, 30);
			return contentPreview + (item.content.length > 30 ? '...' : '');
		}

		// For attributes, use name field
		if (type === 'attribute') {
			return formatName(item.name || '');
		}

		// For insights and others, use name field or show content preview
		if (item.name) {
			return formatName(item.name);
		} else if (item.content) {
			const contentPreview = item.content.substring(0, 30);
			return contentPreview + (item.content.length > 30 ? '...' : '');
		}

		return 'Unnamed Item';
	};

	// Get header text (name or content preview)
	// TODO: use it and remove leading underscore, or remove it
	const _displayName = getHeaderText();

	// No longer using different layouts - normalized for all types

	// Render content based on AMFI type
	const renderContent = () => {
		switch (type) {
			case 'attribute':
				return <AttributeContent item={item} formatName={formatName} />;
			case 'metric':
				return (
					<MetricContent
						item={item}
						formatName={formatName}
						formatMetricValue={formatMetricValue}
					/>
				);
			case 'fact':
				return <FactContent item={item} />;
			case 'insight':
				return <InsightContent item={item} />;
			default:
				return null;
		}
	};

	return (
		<div
			style={{
				...amfiItemStyles.item,
			}}
		>
			{/* Combined content and actions area */}
			<div style={amfiItemStyles.contentContainer}>
				{/* Left side - Content with optional title */}
				<div style={amfiItemStyles.contentLeft}>
					{(type === 'attribute' || type === 'metric') && (
						<div
							style={{
								...amfiItemStyles.itemTitle,
								color: item.is_key ? colors.main : '#333',
							}}
						>
							{getHeaderText()}
						</div>
					)}
					{renderContent()}
				</div>

				{/* Right side - Action Menu */}
				<div>
					<ActionMenu
						item={item}
						type={type}
						onEdit={onEdit}
						onDelete={onDelete}
						onReclassify={onReclassify}
						onToggleKey={handleToggleKey}
					/>
				</div>
			</div>

			{/* Footer with metadata */}
			<Footer type={type} item={item} formatName={formatName} />
		</div>
	);
};

AMFIItem.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string,
		metric_name: PropTypes.string, // For metrics
		category: PropTypes.string, // Made optional
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		content: PropTypes.string,
		unit: PropTypes.string, // For metrics
		is_key: PropTypes.bool,
		source: PropTypes.string,
	}).isRequired,
	type: PropTypes.oneOf(['attribute', 'metric', 'fact', 'insight']).isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onReclassify: PropTypes.func.isRequired,
	onToggleKey: PropTypes.func.isRequired,
};

export default AMFIItem;
